import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getData, setAccessMonitoringVehicleFilter, setFeatureFlag } from '../../api/dataSlice'
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setAccessActions } from '../chatbot/ChatManager'
import { useAccessFooterState } from './useAccessFooterState'
import CameraSelection from '../controls/CameraSelection'
import DatePicker from '../controls/DatePicker'
import VehicleStats from './VehicleStats'

// gated by ACCESS route access (role check skipped on `protected: false`) and per-camera data availability (driven by AI parameter `ACCESS_MONITORING_ENABLED`)
export default function AccessAccordion({ expanded, setExpanded, onMysiteViewChange, isPortrait }) {

    const { ROUTES: { ACCESS }, CAMERAS } = window.conf
    const { t } = useTranslation()
    const { hasRouteAccess, isLoggedIn } = useKeycloak()
    const { isPortrait: mobilePortrait } = useMobileOrientation()
    const { accessMonitoringVehicleFilter } = useSelector(getData)

    const {
        loading,
        availableCameras,
        activeCameraId,
        setSelectedCameraId,
        effectiveTimestamp,
        currentDay,
        minDate,
        maxDate,
        canStepLeft,
        canStepRight,
        updateTimestamp,
        stepLeft,
        stepRight,
        shouldDisableDate
    } = useAccessFooterState(true)

    const { accessMonitoringData, hasAccessData, accessReady } = useGetAccessMonitoringQuery(undefined, {
        selectFromResult: ({ data: accessData, isSuccess, isError }) => ({
            accessMonitoringData: accessData,
            hasAccessData: isSuccess && (CAMERAS ?? []).some(elem => (accessData?.byCameraId?.[elem?.id]?.length || 0) > 0),
            accessReady: isSuccess || isError
        })
    })

    const [pickerOpen, setPickerOpen] = useState(false)

    const dispatch = useDispatch()
    const hasAccessMonitoring = hasRouteAccess(ACCESS) && hasAccessData
    const moreThanOneCam = CAMERAS.length > 1
    const stateRef = useRef({})
    stateRef.current = {
        activeCameraId, currentDay, accessMonitoringVehicleFilter,
        availableCameras, minDate, maxDate, canStepLeft, canStepRight,
        setSelectedCameraId, updateTimestamp, stepLeft, stepRight,
    }

    // chatbot registry — refs feed live state and live callbacks
    useEffect(() => {
        setAccessActions({
            getSelectedCameraId: () => stateRef.current.activeCameraId,
            getAvailableCameraIds: () => stateRef.current.availableCameras || [],
            getDate: () => stateRef.current.currentDay || null,
            getMinDate: () => stateRef.current.minDate?.isValid?.() ? stateRef.current.minDate.format('YYYY-MM-DD') : null,
            getMaxDate: () => stateRef.current.maxDate?.isValid?.() ? stateRef.current.maxDate.format('YYYY-MM-DD') : null,
            getCanStepLeft: () => !!stateRef.current.canStepLeft,
            getCanStepRight: () => !!stateRef.current.canStepRight,
            getVehicleFilter: () => stateRef.current.accessMonitoringVehicleFilter,
            setSelectedCameraId: (id) => stateRef.current.setSelectedCameraId(id),
            setDate: (dateStr) => stateRef.current.updateTimestamp(dateStr),
            setVehicleFilter: (filter) => dispatch(setAccessMonitoringVehicleFilter(Array.isArray(filter) ? filter : [])),
            stepLeft: () => stateRef.current.stepLeft(),
            stepRight: () => stateRef.current.stepRight(),
        })
        return () => setAccessActions(null)
    }, [dispatch])

    // publish flags so the auto-expand hook in ArchiveControls can read them from Redux
    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasAccessMonitoring', value: !!hasAccessMonitoring }))
    }, [dispatch, hasAccessMonitoring])

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'accessReady', value: !!accessReady }))
    }, [dispatch, accessReady])

    const dayEventsForStats = useMemo(() => {
        if (activeCameraId === null || !currentDay) return []
        const items = accessMonitoringData?.byCameraId?.[activeCameraId] ?? []
        return items.filter(elem => typeof elem?.timestamp === "string" && elem.timestamp.slice(0, 10) === currentDay)
    }, [accessMonitoringData, activeCameraId, currentDay])

    const handleVehicleFilterChange = useCallback((checkedRows) => {
        dispatch(setAccessMonitoringVehicleFilter(checkedRows.size > 0 ? [...checkedRows] : []))
    }, [dispatch])

    return (
        <Accordion
            expanded={expanded === 'access'}
            disabled={!hasAccessMonitoring}
            disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
            onChange={(event, newExpanded) => {
                setExpanded(newExpanded ? 'access' : false)
                onMysiteViewChange(newExpanded ? 'access' : null)
            }}
            isPortrait={isPortrait}
        >
            <AccordionSummary>
                {t("pages.access.title")}
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    {moreThanOneCam && (
                        <CameraSelection
                            value={activeCameraId ?? ""}
                            onChange={setSelectedCameraId}
                            availableCameras={availableCameras}
                            disabled={availableCameras?.length === 0}
                        />
                    )}

                    <DatePicker
                        timestamp={effectiveTimestamp}
                        setTimestamp={updateTimestamp}
                        loading={loading}
                        shouldDisableDate={shouldDisableDate}
                        extended={true}
                        minDate={minDate}
                        maxDate={maxDate}
                        open={pickerOpen}
                        setOpen={setPickerOpen}
                        onSelected={() => {
                            if (isMobile && mobilePortrait) setPickerOpen(false)
                        }}
                    />

                    <VehicleStats
                        events={dayEventsForStats}
                        onFilterChange={handleVehicleFilterChange}
                        initialFilter={accessMonitoringVehicleFilter}
                    />
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
